import { development } from 'app/config/constants'
import { CopyButton } from 'component/Button/CopyButton'
import { Space } from 'component/Space'
import { Typo } from 'component/Typo'
import { Vertical } from 'component/Vertical'
import { useSizes } from 'feature/sizes/useSizes'
import { useLatestCallback } from 'hook/useLatestCallback'
import { i18n } from 'i18n'
import { getLang } from 'i18n/config/LanguageDetector'
import { observer } from 'mobx-react-lite'
import { SizeTile } from 'page/StandInferenceScreen/SizeTile'
import { getColorTranslation } from 'page/StartScreen/combine/utils'
import { ReactElement } from 'react'
import { opt } from 'store/opt'
import { store } from 'store/store'
import { getItemShapeLabel, ItemData } from 'type/Inference'
import { Size } from 'type/size'
import { disk } from 'util/typo'
import css from './ItemPane.module.scss'

type Props = {
  item: ItemData
  many?: boolean
  full?: boolean
}

type Desc = {
  title?: string
  text?: string
}

function parseDesc(desc?: string): Desc | undefined {
  desc = desc?.trim()
  if (development) return {
    title: 'трц «саларис» • павильон 334',
    text: 'описание '.repeat(33),
  }
  if (!desc) return undefined

  let eol = desc.indexOf('\n')
  if (eol < 0) eol = desc.length
  const title = desc.substring(0, eol).trim()
  const text = desc.substring(eol).trim()
  return { title, text }
}

function useSize(current: ItemData): Size | undefined {
  if (development) return { name: 'XS', percent: 77, shape: 'top' }

  const item = store.carousel.carousel.item
  if (!item) return undefined

  const index = item.items.findIndex(it => it === current)
  if (index == null || index < 0) return undefined

  const sizes = useSizes(item)
  return sizes[index]
}

export const ItemPane = observer((props: Props): ReactElement | undefined => {
  const { tablet } = store.frame
  const { item, many, full } = props
  const shape = many ? getItemShapeLabel(item) : undefined
  const { article, brand, color, product, description } = item
  const combine = !!opt.options.combine
  const lang = getLang()
  const { hide_name_for_en } = opt.options
  const hide_name = lang === 'en' && hide_name_for_en && combine
  const desc = parseDesc(description)
  const size = 16
  const ellipsis = !full
  const sz = useSize(item)
  const show_size = sz && !full
  const some = !!article || !!color || !!desc

  const onClick = useLatestCallback(() => {
    store.carousel.extend = store.carousel.extend ? undefined : item
  })

  if (!brand && !product) return undefined

  const info = <Vertical gap={8} pad={16} minHeight={tablet ? 120 : 160} wide oh onClick={onClick}>
    <Vertical gap={4} flex="1 0 auto" opposite>
      <Space gap={8}>
        {shape && <Typo className={css.shape} one size={12} upper>
          {shape}
        </Typo>}
        {shape && brand && <Typo one size={16} tertiary>
          {disk}
        </Typo>}
        <Typo one size={16} upper secondary ellipsis={ellipsis}>
          {brand}
        </Typo>
      </Space>
      {product && !hide_name && <Typo size={20} semi upper ellipsis={ellipsis}>
        {product}
      </Typo>}
    </Vertical>
    {some && <Vertical gap={2}>
      {article && <Space gap={8}>
        <Typo size={size} tertiary upper>
          {i18n('Articul')}
        </Typo>
        <Typo size={size} upper ellipsis={ellipsis}>
          {article}
        </Typo>
        <CopyButton size={24} data={article} />
      </Space>}
      {color && <Space gap={8}>
        <Typo size={size} tertiary upper>
          {i18n('Color')}
        </Typo>
        <Typo size={size} upper>
          {getColorTranslation(color)}
        </Typo>
      </Space>}
      {desc && <Vertical>
        {desc.title && <Typo size={size} upper ellipsis={ellipsis}>
          {desc.title}
        </Typo>}
        {desc.text && <Typo size={16} tertiary ellipsis={ellipsis}>
          {desc.text}
        </Typo>}
      </Vertical>}
    </Vertical>}
  </Vertical>

  if (!show_size) return <Space className={css.pane}>
    {info}
  </Space>

  if (!tablet && !many) return <Vertical className={css.pane}>
    {info}
    <Space className={css.horizontal} />
    <Space pad={16} minHeight={160} parallel>
      <SizeTile size={sz} />
    </Space>
  </Vertical>

  return <Space className={css.pane} parallel>
    {info}
    <Space className={css.vertical} />
    <Space pad={16} parallel>
      <SizeTile size={sz} />
    </Space>
  </Space>
})
